import React, { useCallback, ReactNode, useState } from 'react'
import styled, { createGlobalStyle, SimpleInterpolation } from 'styled-components'
import { Box, Input, theme, Text, Button, Flex } from '@containous/faency'
import { Helmet } from 'react-helmet'
import { useDebouncedCallback } from 'use-debounce'
import { Header } from './Header'
import { ServiceList } from './ServiceList'
import { useQueryParam, StringParam } from 'use-query-params'
import useRouter from 'use-react-router'

const BodyGlobalStyle = createGlobalStyle`
  body, #root {
    min-height: 100vh;
    background-color: #f2f3f5;
  }
`

const ResponsiveContainer = styled(Box)`
  position: relative;
  min-height: 100vh;
`

const SideBar = styled(Box)<{ show: boolean }>`
  height: 100%;
  position: fixed;
  margin-top: 64px;
  border-right: 1px solid #ccc;
  width: 300px;
  background-color: #f2f3f5;

  @media (max-width: 800px) {
    width: 100%;
    visibility: hidden;

    ${({ show }): SimpleInterpolation =>
      show &&
      `
      visibility: visible;
    `}
  }
`

const SidebarTitle = styled(Text)`
  letter-spacing: 3px;
  color: ${theme.colors.grays[5]};
  text-transform: uppercase;
  font-weight: 600;
  text-align: left;
  font-size: ${theme.fontSizes[0]};
  display: block;
  padding: ${theme.space[1]} ${theme.space[2]};
  user-select: none;
  box-shadow: 0 12px 12px -10px ${theme.colors.grays[4]};
`

const ContentContainer = styled(Box)<{ open: boolean }>`
  background-color: #f2f3f5;
  padding-top: 64px;

  @media (min-width: 801px) {
    padding-left: 300px;
  }

  @media (max-width: 800px) {
    min-height: 100vh;
    width: 100%;
    padding-top: 104px;
    position: relative;
    left: 100%;
    box-shadow: 0 -5px 16px -16px ${theme.colors.grays[6]};
    transition: opacity 100ms cubic-bezier(0.25, 0.2, 0, 1), transform 200ms cubic-bezier(0.25, 0.2, 0, 1);
    will-change: opacity, transform;
    overflow-x: hidden;
    opacity: 0;

    ${({ open }): SimpleInterpolation =>
      open &&
      `
      -webkit-transform: translateX(-100%);
      transform: translateX(-100%);
      opacity: 1;
    `}
  }
`

const ContentSubNav = styled(Flex)<{ show: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 11;
  background-color: ${theme.colors.white};
  box-shadow: 0 2px 10px -5px ${theme.colors.grays[5]};
  transition: opacity 200ms ease-in-out, transform 200ms ease-in-out;
  will-change: opacity, transform;

  ${({ show }): SimpleInterpolation =>
    show &&
    `
    opacity: 1;
    transform: translateY(64px);
  `}

  @media (min-width: 801px) {
    display: none;
  }
`

export type PageProps = {
  children: ReactNode
  title?: string
}

export const Page: React.FC<PageProps> = ({ children, title }) => {
  const {
    match: {
      params: { name: currentService },
    },
  } = useRouter<{ name?: string }>()
  const [search, setSearch] = useQueryParam('search', StringParam)
  const [isContentOpen, setIsContentOpen] = useState(!!currentService)

  const onChangeSearch = useCallback(
    (search?: string): void => {
      if (!search) {
        setSearch(undefined)
        return
      }

      setSearch(search, 'replaceIn')
    },
    [setSearch],
  )

  const [debouncedCallback] = useDebouncedCallback((search?: string) => {
    onChangeSearch(search)
  }, 500)

  return (
    <>
      <Helmet>
        <title>API Portal {title ? ` - ${title}` : ''}</title>
      </Helmet>
      <BodyGlobalStyle />
      <Header />
      <ResponsiveContainer>
        <SideBar show={!isContentOpen}>
          <Box sx={{ padding: 3 }}>
            <Input
              defaultValue={search || ''}
              placeholder="Search specs by title or description"
              onChange={(e): void => debouncedCallback(e.target.value)}
            />
          </Box>
          <SidebarTitle sx={{ p: 2, color: theme.colors.grays[5] }}>APIs</SidebarTitle>
          <ServiceList
            currentService={currentService}
            openDrawer={(): void => setIsContentOpen(true)}
            search={search}
          />
        </SideBar>
        <ContentSubNav show={isContentOpen}>
          <Button onClick={(): void => setIsContentOpen(false)}>Back</Button>
          <Flex sx={{ flexGrow: 1 }} />
          {currentService && (
            <Flex sx={{ alignItems: 'center', px: [2, 3] }}>
              <Text sx={{ color: theme.colors.grays[5] }}>{currentService}</Text>
            </Flex>
          )}
        </ContentSubNav>
        <ContentContainer open={isContentOpen}>{children}</ContentContainer>
      </ResponsiveContainer>
    </>
  )
}
